import { onMounted, onBeforeUnmount, nextTick } from 'vue';

interface AccessibilityApi {
  initializeAccessibilityObservers: () => void;
  cleanupAccessibilityObservers: () => void;
}

/**
 * Module-scoped state and handlers to enable keyboard accessibility for
 * Kendo Grid column menu triggers (three-dots icon) across the app.
 * - Opens the column menu on Enter/Space when the trigger has focus.
 * - Remembers the trigger opened via mouse or keyboard.
 * - Restores focus to the trigger when Escape is pressed to close the menu.
 * Uses a reference-counted attach/detach to avoid duplicate listeners.
 */
let lastColumnMenuTriggerEl: HTMLElement | null = null;
let docHandlersAttached = false;
let docHandlersRefCount = 0;

function isColumnMenuOpen(): boolean {
  const answer =  !!document.querySelector('.k-animation-container .k-menu, .k-animation-container [role="menu"], .k-popup .k-menu, .k-popup [role="menu"], .k-column-menu, .k-grid-columnmenu, .k-column-menu .k-grid-columnmenu-popup, .k-columnmenu-item-wrapper');
  return answer;
}

function handleEscapeKey(): void {
  // ESC: restore focus to last trigger when a column menu is open
  if (isColumnMenuOpen() && lastColumnMenuTriggerEl) {
    try {
      lastColumnMenuTriggerEl?.focus();
    } catch {
      /* ignore */
    }
  }
}

function handleTabKey(e: KeyboardEvent): void {
  // TAB: check if we're inside a column menu and on the last focusable element
  if (isColumnMenuOpen() && !e.shiftKey) {
    const columnMenuPopup = document.querySelector('.k-column-menu, .k-grid-columnmenu-popup');
    if (columnMenuPopup) {
      const focusableElements = columnMenuPopup.querySelectorAll(
        'button:not([disabled]), [tabindex="0"]:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      const activeElement = document.activeElement;
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      // If current focus is on the last focusable element, prevent tab and focus trigger
      if (activeElement === lastFocusableElement && lastColumnMenuTriggerEl) {
        e.preventDefault();
        e.stopPropagation();
        try {
          lastColumnMenuTriggerEl.focus();
        } catch {
          /* ignore */
        }
      }
    }
  }
}

function handleEnterSpaceKey(e: KeyboardEvent): void {
  // ENTER/SPACE on a column menu trigger: open the menu and remember the trigger
  const target = e.target as HTMLElement | null;
  if (!target) return;

  const anchor = target.closest(
    '.k-grid-header-menu.k-grid-column-menu'
  ) as HTMLAnchorElement | null;
  if (!anchor) return;

  e.preventDefault();
  e.stopPropagation();
  lastColumnMenuTriggerEl = anchor;
  anchor.click();
}

function handleDocumentKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'Escape':
      handleEscapeKey();
      break;
    case 'Tab':
      handleTabKey(e);
      break;
    case 'Enter':
    case ' ':
    case 'Spacebar':
      handleEnterSpaceKey(e);
      break;
    default:
      // No action for other keys
      break;
  }
}

function handleDocumentClick(e: MouseEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  const anchor = target.closest(
    '.k-grid-header-menu.k-grid-column-menu'
  ) as HTMLAnchorElement | null;
  if (anchor) {
    lastColumnMenuTriggerEl = anchor;
  }
}


function attachDocumentHandlers() {
  if (!docHandlersAttached) {
    document.addEventListener('keydown', handleDocumentKeydown, true);
    document.addEventListener('click', handleDocumentClick, true);
    docHandlersAttached = true;
  }
  docHandlersRefCount++;
}

function detachDocumentHandlers() {
  docHandlersRefCount = Math.max(0, docHandlersRefCount - 1);
  if (docHandlersAttached && docHandlersRefCount === 0) {
    document.removeEventListener('keydown', handleDocumentKeydown, true);
    document.removeEventListener('click', handleDocumentClick, true);
    docHandlersAttached = false;
  }
}

export default function useAccessibility(): AccessibilityApi {
  // Helper function to fix NaN values in ARIA attributes
  const fixNaNAriaAttributes = (element: HTMLElement) => {
    // Check the element itself
    if (element.getAttribute('aria-valuenow') === 'NaN') {
      element.setAttribute('aria-valuenow', '0');
    }
    if (element.getAttribute('aria-valuetext') === 'NaN') {
      element.setAttribute('aria-valuetext', '0');
    }
    if (element.getAttribute('aria-rowindex') === 'NaN') {
      element.removeAttribute('aria-rowindex');
    }

    // Check all descendant elements with NaN ARIA attributes
    const elementsWithNaN = element.querySelectorAll(
      '[aria-valuenow="NaN"], [aria-valuetext="NaN"], [aria-rowindex="NaN"]'
    );
    elementsWithNaN.forEach((el) => {
      if (el instanceof HTMLElement) {
        if (el.getAttribute('aria-valuenow') === 'NaN') {
          el.setAttribute('aria-valuenow', '0');
        }
        if (el.getAttribute('aria-valuetext') === 'NaN') {
          el.setAttribute('aria-valuetext', '0');
        }
        if (el.getAttribute('aria-rowindex') === 'NaN') {
          el.removeAttribute('aria-rowindex');
        }
      }
    });
  };

  const observerCallback = (mutations: MutationRecord[]) => {
    mutations.forEach((mutation) => {
      // Handle attribute changes for aria-valuenow and aria-valuetext
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
        const target = mutation.target;
        const attrName = mutation.attributeName;

        if (attrName === 'aria-valuenow' && target.getAttribute('aria-valuenow') === 'NaN') {
          target.setAttribute('aria-valuenow', '0');
        }
        if (attrName === 'aria-valuetext' && target.getAttribute('aria-valuetext') === 'NaN') {
          target.setAttribute('aria-valuetext', '0');
        }
        if (attrName === 'aria-rowindex' && target.getAttribute('aria-rowindex') === 'NaN') {
          target.removeAttribute('aria-rowindex');
        }
      }

      // Handle added nodes
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        // Fix NaN ARIA attributes on newly added elements
        fixNaNAriaAttributes(node);

        // Handle dialog focus
        if (node.classList.contains('k-dialog-wrapper')) {
          nextTick(() => {
            node.focus();
          });
        }

        // Handle pager spinbutton accessibility
        const pagerSpinButtons = node.querySelectorAll('.k-pager input[role="spinbutton"]');
        if (pagerSpinButtons.length > 0) {
          pagerSpinButtons.forEach((input) => {
            if (input instanceof HTMLElement) {
              input.setAttribute('aria-label', 'page number');
            }
          });
        }

        // Check for dynamically added pager content
        if (node.classList.contains('k-pager')) {
          const spinButtons = node.querySelectorAll('input[role="spinbutton"]');
          spinButtons.forEach((input) => {
            if (input instanceof HTMLElement) {
              input.setAttribute('aria-label', 'page number');
            }
          });
        }
      });

    });
  };

  const mutationObserver = new MutationObserver(observerCallback);

  const initializeAccessibilityObservers = () => {
    // Fix any existing NaN ARIA attributes on page load
    const existingElementsWithNaN = document.querySelectorAll(
      '[aria-valuenow="NaN"], [aria-valuetext="NaN"], [aria-rowindex="NaN"]'
    );
    existingElementsWithNaN.forEach((el) => {
      if (el instanceof HTMLElement) {
        if (el.getAttribute('aria-valuenow') === 'NaN') {
          el.setAttribute('aria-valuenow', '0');
        }
        if (el.getAttribute('aria-valuetext') === 'NaN') {
          el.setAttribute('aria-valuetext', '0');
        }
        if (el.getAttribute('aria-rowindex') === 'NaN') {
          el.removeAttribute('aria-rowindex');
        }
      }
    });

    // Start observing the body for dialog appearances and pager elements
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'aria-valuenow', 'aria-valuetext', 'aria-rowindex'],
    });

    // Attach global column menu keyboard/click handlers (ref-counted)
    attachDocumentHandlers();
  };

  const cleanupAccessibilityObservers = () => {
    mutationObserver.disconnect();
    // Detach global handlers when no instance needs them
    detachDocumentHandlers();
  };

  onMounted(() => {
    initializeAccessibilityObservers();
  });

  onBeforeUnmount(() => {
    cleanupAccessibilityObservers();
  });

  return {
    initializeAccessibilityObservers,
    cleanupAccessibilityObservers,
  };
}
